import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { TbExternalLink } from 'react-icons/tb'
import { Button } from 'antd'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { Message } from 'hooks/useGlobalContext/useMessageStore'

const ENTER_DURATION = 300
const EXIT_DURATION = 300

const MessageBar = ({
  msg,
  onDismiss
}: {
  msg: Message
  onDismiss: (id: string) => void
}) => {
  const [state, setState] = useState<'entering' | 'visible' | 'exiting'>(
    'entering'
  )
  const isExitingRef = useRef(false)
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timer = setTimeout(() => setState('visible'), ENTER_DURATION)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (msg.type !== 'message') {
      return
    }

    const duration = (msg.duration ?? 3) * 1000
    exitTimerRef.current = setTimeout(() => triggerExit(), duration)

    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current)
      }
    }
  }, [msg.type, msg.duration])

  const triggerExit = () => {
    if (isExitingRef.current) {
      return
    }

    isExitingRef.current = true
    const wrapper = wrapperRef.current
    if (wrapper) {
      wrapper.style.height = `${wrapper.scrollHeight}px`
      // Force reflow so the browser registers the explicit height
      wrapper.offsetHeight // eslint-disable-line no-unused-expressions
      wrapper.style.height = '0px'
      wrapper.style.marginTop = '0px'
      wrapper.style.overflow = 'hidden'
    }

    setState('exiting')
    setTimeout(() => onDismiss(msg.id), EXIT_DURATION)
  }

  const handleDismiss = () => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current)
    }

    triggerExit()
  }

  return (
    <Wrapper ref={wrapperRef} state={state}>
      <Bar state={state}>
        <div>
          <span>{msg.content}</span>
        </div>
        <Actions>
          {msg.action &&
            (msg.action.to ? (
              <Link to={msg.action.to}>
                <ActionButton>
                  {msg.action.label}
                  <TbExternalLink style={{ fill: '#4a4a68' }} size='16' />
                </ActionButton>
              </Link>
            ) : (
              <ActionButton onClick={msg.action.onClick}>
                {msg.action.label}
              </ActionButton>
            ))}
          {msg.type === 'message' && (
            <ActionButton onClick={handleDismiss}>Dismiss</ActionButton>
          )}
        </Actions>
      </Bar>
    </Wrapper>
  )
}

const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const fadeOutRight = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(100px);
  }
`

type AnimState = 'entering' | 'visible' | 'exiting'

const Wrapper = styled.div<{ state: AnimState }>`
  transition: height ${EXIT_DURATION}ms ease, margin-top ${EXIT_DURATION}ms ease;

  & + & {
    margin-top: 8px;
  }
`

const Bar = styled.div<{ state: AnimState }>`
  width: 100%;
  padding: 12px 16px;

  background-color: #e9e8fc;
  border: 1px solid #4a4a68;
  border-radius: 6px;

  color: #4a4a68;
  font-weight: 500;
  font-size: 13px;

  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  animation: ${({ state }) =>
      state === 'entering'
        ? fadeInDown
        : state === 'exiting'
        ? fadeOutRight
        : 'none'}
    ${({ state }) =>
      state === 'entering'
        ? `${ENTER_DURATION}ms`
        : state === 'exiting'
        ? `${EXIT_DURATION}ms`
        : '0s'}
    ease forwards;
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
`

export default MessageBar
